/* eslint-disable @typescript-eslint/no-explicit-any */
/* ----------------------------------
 - plain.js -
 JS全般で利用されるグローバル処理定義
---------------------------------- */
import {
  Level
} from '@/lib/definition/constants'
import Param from '@/lib/definition/variables'
import moment from 'moment'

// ## ログユーティリティ
// variables.jsでログ出力レベルを変更させる事が可能です。
export class Log {
  static debug(msg: any) {
    if (this.valid(Level.DEBUG)) this.console('[DEBUG] ', msg)
  }

  static info(msg: any) {
    if (this.valid(Level.INFO)) this.console('[INFO] ', msg)
  }

  static warn(msg: any) {
    if (this.valid(Level.WARN)) this.console('[WARN] ', msg)
  }

  static error(msg: any) {
    if (this.valid(Level.ERROR)) this.console('[ERROR] ', msg)
  }

  static valid(checkLevel: number) {
    return Param.System.logLevel <= checkLevel
  }

  static console(prefix: string, obj: string) {
    // eslint-disable-next-line no-console
    if (console) console.log(prefix, obj)
  }
}

// ## UI側セッションユーティリティ
// WebStorage(LocalStrage)利用を前提としたセッション概念を提供します。
export class Session {
  // ログインさせます。引数に与えたハッシュはWebStrage(Local)に保存されます。
  static login(sessionHash: string) {
    this.logout()
    this.valueSet(Param.Session.key, sessionHash)
  }

  // ログイン中のセッション情報を取得します。key未指定時はログイン情報を返します。
  static value(key: string | null = null) {
    const v = localStorage.getItem(key || Param.Session.key)
    return v ? JSON.parse(v) : null
  }

  // セッション情報に値を設定します。リークしやすいため、安易な利用は避けてください。
  static valueSet(key: string, value: any) {
    localStorage.setItem(key, value ? JSON.stringify(value) : '')
  }

  // ログアウトさせます。個別にvalueSetした情報は忘れずにdeleteしてください。
  static logout() {
    this.remove()
  }

  // セッション情報の値を削除します。
  static remove(key: string | null = null) {
    localStorage.removeItem(key || Param.Session.key)
  }

  // セッションを持っているか
  static hasSession() {
    return this.value()
  }
}

// ## フォーマットユーティリティ
export class Formatter {
  static format(type: string, value: any) {
    switch (type) {
      case 'day':
        return value ? moment(value).format('YYYY-MM-DD') : ''
      case 'amount':
        return (value || value === 0) ? value.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') : ''
      default:
        return value.toString()
    }
  }
}

// ## エラーユーティリティ
export class JJError extends Error {
  public ErrorCode: number | null
  public InnerError: Error | null
  constructor(message: string, ErrorCode: number | null = null, InnerError: Error | null = null) {
    super(message)
    this.name = 'JJError'
    this.message = message
    this.ErrorCode = ErrorCode
    this.InnerError = InnerError
  }
}

// ## グローバルユーティリティ
export class Utils {
  // URLの引数を解析してハッシュを返します
  static parseQuery(url = null) {
    const target = url || window.location.search
    if (!target) return {}
    const idx = target.indexOf('?')
    const query = idx === -1 ? target : target.substring(idx + 1)
    const values = query.split('&')
    const ret: any = {}
    Array.from(values).map((v) => v.split('=')).filter((pair) => pair && pair.length === 2)
      .forEach((pair) => (ret[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1])))
    return ret
  }

  // 引数に与えたハッシュオブジェクトでネストされたものを「.」付の一階層へ変換します。(引数は上書きしません)
  // {a: {b: {c: 'd'}}} -> {'a.b.c': 'd'}
  // see http://stackoverflow.com/questions/19098797/fastest-way-to-flatten-un-flatten-nested-json-objects
  static flatten(data: any) {
    const ret: any = {}
    const recurse = (v: any, prop: any) => {
      if (Object(v) !== v) {
        ret[prop] = v
      } else if (Array.isArray(v)) {
        Array.from(v).forEach((value, i) => recurse(value, `prop[${i}]`))
        if (v.length === 0) ret[prop] = []
      } else {
        let empty = true
        Object.keys(v).forEach((key) => {
          const value = v[key]
          empty = false
          recurse(value, prop ? `${prop}.${key}` : key)
        })
        if (empty && prop) ret[prop] = {}
      }
    }
    recurse(data, '')
    return ret
  }

  // ドット繋ぎのパスからオブジェクトを取得する
  static getObject(object: any, path: string): any {
    if (!path) return object
    if (object === null || typeof object !== 'object') return object

    const [pathHead, pathTail] = path.split(/\.(.+)/)
    return this.getObject(object[pathHead], pathTail)
  }

  // オブジェクトのパス（ドットつなぎ）を取得する
  static pickObject(object: any, properties: Array<string>) {
    const map: { [key: string]: any } = {}
    return properties.reduce((pickedObject, property) => {
      pickedObject[property] = object[property]
      return pickedObject
    }, map)
  }

  // 全角カナ-半角カナ変換
  static zen2han(str: string) {
    const map: { [key: string]: string } = {
      ア: 'ｱ',
      イ: 'ｲ',
      ウ: 'ｳ',
      エ: 'ｴ',
      オ: 'ｵ',
      カ: 'ｶ',
      キ: 'ｷ',
      ク: 'ｸ',
      ケ: 'ｹ',
      コ: 'ｺ',
      サ: 'ｻ',
      シ: 'ｼ',
      ス: 'ｽ',
      セ: 'ｾ',
      ソ: 'ｿ',
      タ: 'ﾀ',
      チ: 'ﾁ',
      ツ: 'ﾂ',
      テ: 'ﾃ',
      ト: 'ﾄ',
      ナ: 'ﾅ',
      ニ: 'ﾆ',
      ヌ: 'ﾇ',
      ネ: 'ﾈ',
      ノ: 'ﾉ',
      ハ: 'ﾊ',
      ヒ: 'ﾋ',
      フ: 'ﾌ',
      ヘ: 'ﾍ',
      ホ: 'ﾎ',
      マ: 'ﾏ',
      ミ: 'ﾐ',
      ム: 'ﾑ',
      メ: 'ﾒ',
      モ: 'ﾓ',
      ヤ: 'ﾔ',
      ユ: 'ﾕ',
      ヨ: 'ﾖ',
      ラ: 'ﾗ',
      リ: 'ﾘ',
      ル: 'ﾙ',
      レ: 'ﾚ',
      ロ: 'ﾛ',
      ワ: 'ﾜ',
      ヲ: 'ｦ',
      ン: 'ﾝ',
      ァ: 'ｧ',
      ィ: 'ｨ',
      ゥ: 'ｩ',
      ェ: 'ｪ',
      ォ: 'ｫ',
      ッ: 'ｯ',
      ャ: 'ｬ',
      ュ: 'ｭ',
      ョ: 'ｮ',
      ガ: 'ｶﾞ',
      ギ: 'ｷﾞ',
      グ: 'ｸﾞ',
      ゲ: 'ｹﾞ',
      ゴ: 'ｺﾞ',
      ザ: 'ｻﾞ',
      ジ: 'ｼﾞ',
      ズ: 'ｽﾞ',
      ゼ: 'ｾﾞ',
      ゾ: 'ｿﾞ',
      ダ: 'ﾀﾞ',
      ヂ: 'ﾁﾞ',
      ヅ: 'ﾂﾞ',
      デ: 'ﾃﾞ',
      ド: 'ﾄﾞ',
      バ: 'ﾊﾞ',
      ビ: 'ﾋﾞ',
      ブ: 'ﾌﾞ',
      ベ: 'ﾍﾞ',
      ボ: 'ﾎﾞ',
      パ: 'ﾊﾟ',
      ピ: 'ﾋﾟ',
      プ: 'ﾌﾟ',
      ペ: 'ﾍﾟ',
      ポ: 'ﾎﾟ',
      ヴ: 'ｳﾞ',
      ヷ: 'ﾜﾞ',
      ヺ: 'ｦﾞ',
      '。': '｡',
      '、': '､',
      ー: 'ｰ',
      '「': '｢',
      '」': '｣',
      '・': '･'
    }
    const reg = new RegExp('(' + Object.keys(map).join('|') + ')', 'g')
    return str
      .replace(reg, function (match) {
        return map[match]
      })
      .replace(/[Ａ-Ｚａ-ｚ０-９！＂＃＄％＆＇（）＊＋，－．／：；＜＝＞？＠［＼］＾＿｀｛｜｝]/g, function (s) {
        return String.fromCharCode(s.charCodeAt(0) - 65248)
      })
      .replace(/[‐－―]/g, '-')
      .replace(/[～〜]/g, '~')
      .replace(/[[:blank:]]/g, ' ')
  }
}
type AjaxEncodedParamValue = string | number | boolean | null | undefined
type AjaxEncodedParams = { [key: string]: AjaxEncodedParamValue }

// ## Ajaxユーティリティ
export class Ajax {
  // Web APIのVersion (V1/V2) を取得する
  static apiVer() {
    const curPath = location.pathname
    let curPathArr = curPath.split('/').filter(function (s) {
      return s !== ''
    })
    const exclude = ['CMJPSPC', 'CMJPSTB', 'CMJPSTV', 'CMJPSBackend']
    curPathArr = curPathArr.filter(i => exclude.indexOf(i) === -1)
    return curPathArr[0] === 'V1' ? 'V1' : 'V2'
  }

  // Web APIのパスを取得する
  static apiUrl(path: string) {
    // var apiVer = this.apiVer();
    // var v1Api = ['/Token', '/api/Account/UserInfo'];
    // path = (apiVer === 'V2' && !v1Api.includes(path)) ? path.replace('/api/', '/api/V2/') : path;
    return `${Param.Site.baseApiUrl}${path}`
  }

  // Web APIアクセス用のオプションを作成する
  static apiOption(accessToken: string | null) {
    let options = {}
    if (Param.Site.useWinAuth) {
      return {}
    }
    if (accessToken != null) {
      options = {
        Authorization: 'Bearer ' + accessToken
      }
    }
    return options
  }

  private static encodeParams(params: AjaxEncodedParams) {
    return Object.keys(params)
      .filter((key) => params[key] != null)
      .map((key) => key + '=' + encodeURIComponent(String(params[key])))
      .join('&')
      .toString()
  }

  // Get
  static async get<T>(path: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {}): Promise<{ response: Response; data: T }> {
    const body = this.encodeParams(params)
    if (body != null && body.length > 0) {
      path += '?' + body
    }
    const url: string = this.apiUrl(path)
    const headers = Object.assign(this.apiOption(accessToken), options)
    const response = await fetch(url, {
      method: 'GET',
      headers
    })
    const data: T = (await response.json()) as T
    return { response, data }
  }

  // Get
  static async delete<T>(path: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {}): Promise<{ response: Response; data: T }> {
    const body = this.encodeParams(params)
    if (body != null && body.length > 0) {
      path += '?' + body
    }
    const url: string = this.apiUrl(path)
    const headers = Object.assign(this.apiOption(accessToken), options)
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    })
    const data: T = (await response.json()) as T
    return { response, data }
  }
  static async getHandleError<T>(path: string, generalMessage: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {}): Promise<{ response: Response; data: T }> {
    try {
      const retval = await this.get<T>(path, params, accessToken, options)
      if (!retval.response.ok) {
        // エラーメッセージが取れなかった場合
        throw new JJError(generalMessage, null, null)
      }
      return retval
    } catch (e) {
      throw new JJError(generalMessage, null, e as Error)
    }
  }
  static async deleteHandleError<T>(path: string, generalMessage: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {}): Promise<{ response: Response; data: T }> {
    try {
      const retval = await this.delete<T>(path, params, accessToken, options)
      if (!retval.response.ok) {
        // エラーメッセージが取れなかった場合
        throw new JJError(generalMessage, null, null)
      }
      return retval
    } catch (e) {
      throw new JJError(generalMessage, null, e as Error)
    }
  }
  // Get
  static async getDownload(path: string, params: AjaxEncodedParams = {}, accessToken = null, options: { [key: string]: string } = {}, filename: string) {
    const body = this.encodeParams(params)
    if (body != null && body.length > 0) {
      path += '?' + body
    }
    const url = this.apiUrl(path)
    const headers = Object.assign(this.apiOption(accessToken), options)
    const res = await fetch(url, {
      method: 'GET',
      headers
    })
    if (res.ok !== true) {
      return false
    }
    const blob = await res.blob()
    const url2 = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url2
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    return true
  }

  static async getDownloadHandleError(path: string, generalMessage: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {}, filename: string) {
    try {
      const body = this.encodeParams(params)
      if (body != null && body.length > 0) {
        path += '?' + body
      }
      const url = this.apiUrl(path)
      const headers = Object.assign(this.apiOption(accessToken), options)
      const res = await fetch(url, {
        method: 'GET',
        headers
      })
      if (!res.ok) {
        // エラーメッセージが取れなかった場合
        throw new JJError(generalMessage, null, null)
      }
      const blob = await res.blob()
      const url2 = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url2
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      return true
    } catch (e) {
      throw new JJError(generalMessage, null, e)
    }
  }

  // Post
  static async post<T>(path: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded'
  }) {
    const url = this.apiUrl(path)
    const body = this.encodeParams(params)
    const headers = Object.assign(this.apiOption(accessToken), options)
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    })
    const data = await response.json() as T
    return { response, data }
  }

  static async postHandleError<T>(path: string, generalMessage: string, params: AjaxEncodedParams = {}, accessToken: string | null = null, options: { [key: string]: string } = {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded'
  }) {
    try {
      const retval = await this.post<T>(path, params, accessToken, options)
      if (!retval.response.ok) {
        // エラーメッセージが取れなかった場合
        throw new JJError(generalMessage, null, null)
      }
      return retval
    } catch (e) {
      if (JJError.name === e.constructor.name) {
        throw e
      }
      throw new JJError(generalMessage, null, e)
    }
  }

  // Post
  static async postJson<T>(path: string, params: { [key: string]: string } = {}, accessToken: string | null = null, options: { [key: string]: string } = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }) {
    const url = this.apiUrl(path)
    const body = JSON.stringify(params)
    const headers = Object.assign(this.apiOption(accessToken), options)
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body
    })
    const data = await response.json() as T
    return { response, data }
  }

  static async postJsonHandleError<T>(path: string, generalMessage: string, params: { [key: string]: string } = {}, accessToken: string | null = null, options: { [key: string]: string } = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }) {
    try {
      const retval = await this.postJson<T>(path, params, accessToken, options)
      if (!retval.response.ok) {
        // エラーメッセージが取れなかった場合
        throw new JJError(generalMessage, null, null)
      }
      return retval
    } catch (e) {
      if (JJError.name === e.constructor.name) {
        throw e
      }
      throw new JJError(generalMessage, null, e)
    }
  }
}
